// CoachReportMixin.ts

import { Component, Prop, Mixins } from 'vue-property-decorator';
import { MyCoachMixin, VuetifyMixin, BAIconsMixin, CoachReportScoreMixin, CoachRoutingMixin, ShowcaseEventMixin } from '@/mixins';
import { AthleteAutocompleteInfo } from '@best-athletes/ba-types';
import { PlayerTypeValues, SportName } from '@/../types/enums'
import { BAEventModel, PartialAthleteProfile, PartialCoachProfile, PartialTeam, PlayerOnTeam, SportModel, PartialSport, AthleteProfileModel, TeamModel } from '@/models';
import { athleteApi } from '@/api/AthleteApi';
import { coachApi } from '@/api/CoachApi';
import { isValidUUID } from '@/pipes';
import { teamApi } from '@/api/TeamApi';
import { sportApi } from "@/api/SportApi";
import { ObjectCache } from '@/helpers';
import { PartialOrganization } from '@/models/organization/OrganizationModel';

@Component
export class CoachReportMixin extends Mixins(
    VuetifyMixin,
    BAIconsMixin,
    MyCoachMixin,
    CoachRoutingMixin,
    CoachReportScoreMixin,
    ShowcaseEventMixin,
){
	PlayerTypeValues = PlayerTypeValues;

	@Prop() reportId: string;
	@Prop() athleteId: string;
	@Prop() teamId: string;
	@Prop() coachId: string;
	athlete: PartialAthleteProfile;
	coach: PartialCoachProfile;
	team: PartialTeam;
	player: PlayerOnTeam;
	sport: PartialSport;
	athletesCache: ObjectCache<PartialAthleteProfile> = new ObjectCache<PartialAthleteProfile>();
	coachesCache: ObjectCache<PartialCoachProfile> = new ObjectCache<PartialCoachProfile>();
	teamsCache: ObjectCache<PartialTeam> = new ObjectCache<PartialTeam>();
	orgsCache: ObjectCache<PartialOrganization> = new ObjectCache<PartialOrganization>();
	sportsCache: ObjectCache<SportModel> = new ObjectCache<SportModel>();

	athleteLink: AthleteAutocompleteInfo;

    isReportLoaded: boolean = false;
    isReportLoading: boolean = false;
    isReportUpdating: boolean = false;
	isReportEditing: boolean = false;
	isReportChanging: boolean = false;
	isReportChanged: boolean = false;
	isReportComplete: boolean = false;

    showHelp: boolean = false;
    errorMessage: string;

	async mounted() {
		await this.initializeReport();
		await this.loadReport();
	}

	get IsLoading(): boolean {
		return this.isReportLoading || this.isReportUpdating;
	}

	// if a report id is provided, the report was previously saved
	get HasReportId(): boolean {
		return this.IsNotEmpty( this.reportId );
	}

	get AthleteFullName(): string {
		return this.athlete?.FullName;
	}

	async loadAthlete(): Promise<PartialAthleteProfile> {
		if( this.IsEmpty(this.athleteId) ) return undefined;

		this.athlete = this.athletesCache.get(this.athleteId);
		if( this.IsNotEmpty(this.athlete) ) return this.athlete;

		this.athlete = await athleteApi.getPublicProfile(this.athleteId);
		this.athletesCache.set(this.athlete);
		return this.athlete;
	}
	async loadCoach(): Promise<PartialCoachProfile> {
		if( this.IsEmpty(this.coachId) ) return undefined;

		this.coach = this.coachesCache.get(this.coachId);
		if( this.IsNotEmpty(this.coach) ) return this.coach;

		this.coach = await coachApi.findById(this.coachId);
		this.coachesCache.set(this.coach);
		return this.coach;
	}
	async loadTeam(): Promise<PartialTeam> {
		if( !isValidUUID(this.teamId) ) this.teamId = this.athlete?.currentTeam;

		// handle athleteTeams, which use team name as the teamId
		const athleteTeam = this.athlete?.athleteTeams?.find(t => t.name === this.teamId);
		if( this.IsNotEmpty(athleteTeam) ) {
			this.team = new TeamModel().fromAthleteTeam(athleteTeam);
			return this.team;
		}
		
		if( !isValidUUID(this.teamId) && this.IsNotEmpty(this.athlete?.teams) ) this.teamId = this.athlete.teams.slice(-1)[0].id;
		if( !isValidUUID(this.teamId) ) return undefined;

		this.team = this.teamsCache.get(this.teamId);
		if( this.IsNotEmpty(this.team) ) return this.team;

		this.team = await teamApi.findById(this.teamId);
		this.teamsCache.set(this.team);
		if( this.IsEmpty(this.team) ) return undefined;

		// load team player
		this.player = this.team.players.find(p => p.athleteId === this.athleteId);
		return this.team;
	}
	async loadSport(): Promise<PartialSport> {
		if( this.IsEmpty(this.sport) ) this.sport = this.athlete?.Sport;
		if( this.IsEmpty(this.sport) ) this.sport = await sportApi.findByName(this.team?.Sport);
		if( this.IsEmpty(this.sport) ) this.sport = this.IsNotEmptyArray(this.coach?.sports)? await sportApi.findByName( this.coach.sports[0]) : undefined;
		if( this.IsEmpty(this.sport) ) this.sport = await sportApi.findByName(SportName.Soccer);
		return this.sport;
	}
	async initializeReport() {
		if( this.IsEmpty(this.coachId) && (this.CoachAppActive || this.AdminAppActive) ) this.coachId = this.MyCoachId;

		this.isReportLoaded = false;
		this.isReportLoading = true;
		await this.createNewReport();
		await this.loadAthlete();
		await this.loadCoach();
		await this.loadTeam();
		await this.loadSport();
		await this.populateNewReport();
		await this.updateOnChange();
		this.isReportLoading = false
		this.isReportLoaded = true;
	}
	async loadReport() {
		this.isReportLoaded = false;
		this.isReportLoading = true;
		if( this.HasReportId ) {
			this.isReportLoaded = false;
            await this.findReport();
			this.isReportLoaded = true;
		}
		else if( this.isReportLoaded ) {
			await this.updateReport();
		}
        await this.updateFromReport();
		await this.updateOnChange();
		
		this.isReportLoading = false;
		this.isReportLoaded = true;
	}

    async resetAthleteLink() {
		this.athleteId = undefined;
		this.teamId = undefined;
		this.athlete = undefined;
		this.team = undefined;
		this.player = undefined;
	}
    async loadCurrentEvent() {
		// load showcase event if there is a current event in progress
		await this.loadCurrentShowcaseEvents();

        if( this.HasShowcaseEvents ) {
            for( const event of this.showcaseEvents ) {
                if( event.IsToday ) this.onEventLoaded(event);
            }
		}
    }

    /* override the following functions */
	async createNewReport() {}
	async populateNewReport() {}
    async findReport() {}
    async updateReport() {}
    async updateFromReport() {}
	async updateOnChange() {}
    async onSubmitStart() {}
    async onEventLoaded(event: BAEventModel) {}

	async onReportChanged() {
		this.isReportChanging = true;
		await this.updateOnChange();
		this.isReportChanged = true;
		this.isReportChanging = false;
	}

	async onSelectAthlete(athlete: AthleteProfileModel) {
		this.isReportUpdating = true;

		await this.resetAthleteLink();
		this.athleteId = athlete?.id;
		await this.initializeReport();
		await this.updateReport();

		this.isReportUpdating = false;
	}

	async initalSubmit(validate: () => Promise<boolean>): Promise<void> {
		const isValid = await validate();
		if (!isValid) {
			this.errorMessage = 'Please ensure that all required fields and scores are provided';
			this.$vuetify.goTo(0);
		}
		else {
			this.onSubmitStart()
		}
	}

	get IsSoccer(): boolean {
		if( this.IsEmpty(this.sport) ) return false;
		return this.sport.name === SportName.Soccer;
	}

	isWeblinksChanging: boolean = false;
	onWeblinkAdd(report: any) {
		this.isWeblinksChanging = true;
		if( this.IsEmpty(report.weblinks) ) report.weblinks = [];
		report.weblinks.unshift({label:'', url:''});
		this.isReportChanged = true;
		this.isWeblinksChanging = false;
	}
	onWeblinkDelete(index: number, report: any) {
		this.isWeblinksChanging = true;
		report.weblinks.splice(index, 1);
		this.isReportChanged = true;
		this.isWeblinksChanging = false;
	}
	onWeblinkChange() {
		this.isWeblinksChanging = true;
		this.isReportChanged = true;
		this.isWeblinksChanging = false;
	}

}
